import React, { useMemo } from 'react';
import { BaseSelector, BaseSelectorProps } from '../BaseSelector';
import { Option } from '../CustomDropdown';
import { BLOOMS_MAP } from './BloomsSelector';
import { useBloomsRefinement } from './useBloomsRefinement';
import CustomOptionWithCount from '../CustomOptionWithCount';

interface BloomsSelectorWithCountsProps
  extends Omit<BaseSelectorProps, 'label' | 'options'> {
  // If true, will attempt to use Algolia refinement hook for counts
  enableCounts?: boolean;
  // Manual override for count data (useful for testing or custom data sources)
  countData?: Array<{ level: number; count: number }>;
}

/**
 * Enhanced BloomsSelector that displays counts from Algolia refinements
 * Must be used within a SharedAlgoliaProvider if enableCounts is true
 */
export const BloomsSelectorWithCounts: React.FC<BloomsSelectorWithCountsProps> = ({
  enableCounts = false,
  countData,
  ...props
}) => {
  const bloomsRefinement = useBloomsRefinement();

  const optionsWithCounts = useMemo((): Option[] => {
    // Generate base options
    const baseOptions = Object.entries(BLOOMS_MAP).map(([level, name]) => ({
      label: name, // Just the name for the option label
      value: level,
    }));

    // If counts are not enabled, return base options
    if (!enableCounts) {
      return baseOptions;
    }

    // Add counts from manual data if provided
    if (countData) {
      return baseOptions.map((option) => {
        const countItem = countData.find(
          (item) => item.level === parseInt(option.value, 10),
        );
        return {
          ...option,
          count: countItem?.count,
        };
      });
    }

    // Add counts from refinement hook if available and enabled
    if (bloomsRefinement && enableCounts) {
      return baseOptions.map((option) => {
        const level = parseInt(option.value, 10);
        const count = bloomsRefinement.getCountForLevel(level);
        return {
          ...option,
          count: count > 0 ? count : 0,
        };
      });
    }

    // Fallback to base options if no count data available
    return baseOptions;
  }, [enableCounts, countData, bloomsRefinement]);

  // Custom label functions to match BloomsSelector behavior
  const optionLabel = (option: Option) => {
    if (enableCounts && option.count !== undefined) {
      return (
        <CustomOptionWithCount
          value={option.value}
          label={option.label}
          count={option.count}
        />
      );
    }
    return `${option.value} - ${option.label}`;
  };
  const selectedLabel = (selected: Option) =>
    `Blooms: ${selected.value} - ${selected.label}`;

  return (
    <BaseSelector
      {...props}
      label="Blooms"
      options={optionsWithCounts}
      optionLabel={optionLabel}
      selectedLabel={selectedLabel}
      tooltip="Bloom's Taxonomy"
    />
  );
};

export default BloomsSelectorWithCounts;
